import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import routes from '@commons/routes';
import { useIntl } from 'react-intl';
import { myFetch } from '@commons/fetch';
import { useLoading } from '@contexts/LoadingContext';
import { useAppDispatch, useAppSelector } from '@store';
import { fetchModels } from '@store/slices/modelsSlice';
import {
  Container,
  Title,
  Button,
  Group,
  Table,
  Menu,
  ActionIcon,
  Center,
  Loader,
  TextInput,
  NativeSelect,
  Stack,
} from '@mantine/core';
import {
  IconSettings,
  IconTrash,
  IconPencil,
  IconCopyPlus,
  IconEdit,
  IconFileImport,
  IconFilePlus,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { DeleteModal } from './Modals/DeleteModal';
import { notifications } from '@mantine/notifications';
import { RenameModal } from './Modals/RenameModal';
import { useTimeAgo } from '@commons/timeAgo';
import { ImportFieldsModal } from './Modals/ImportFieldsModal';

export const UserModels = () => {
  const intl = useIntl();
  const timeAgo = useTimeAgo();
  const { username } = useParams();
  const { setIsLoading } = useLoading();
  const dispatch = useAppDispatch();
  const { models, isLoading: isLoadingModels } = useAppSelector(
    (state) => state.models
  );
  const [importFieldsItem, setImportFieldsItem] = useState<any>(null);
  const [renameModel, setRenameModel] = useState<any>(null);
  const [deleteModel, setDeleteModel] = useState<any>(null);
  const [filterName, setFilterName] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [sortField, setSortField] = useState<'name' | 'category' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();

  const handleDuplicate = async (model: any) => {
    try {
      setIsLoading(true);
      const response = await myFetch(
        `/${model.owner}/models/${model.id}/duplicate`,
        {
          method: 'POST',
        }
      );

      // Refresh models from Redux after duplication
      if (username) {
        await dispatch(fetchModels(username)).unwrap();
      }

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'duplicatedModel' },
          { name: response.data.name }
        ),
      });
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique types and categories for filter options
  const uniqueTypes = useMemo(() => {
    const types = models.map((m) => m.type).filter(Boolean);
    return Array.from(new Set(types)).sort();
  }, [models]);

  const uniqueCategories = useMemo(() => {
    const categories = models.map((m) => m.category).filter(Boolean);
    return Array.from(new Set(categories)).sort();
  }, [models]);

  // Filter and sort models based on filter inputs
  const filteredModels = useMemo(() => {
    let filtered = models.filter((model) => {
      // Filter by name
      if (
        filterName &&
        !model.name.toLowerCase().includes(filterName.toLowerCase())
      ) {
        return false;
      }

      // Filter by type
      if (filterType && model.type !== filterType) {
        return false;
      }

      // Filter by category
      if (filterCategory && model.category !== filterCategory) {
        return false;
      }

      return true;
    });

    // Sort by field if sort field is set
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let valueA: string;
        let valueB: string;

        if (sortField === 'name') {
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
        } else {
          // category
          valueA = (a.category || '').toLowerCase();
          valueB = (b.category || '').toLowerCase();
        }

        if (sortOrder === 'asc') {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      });
    }

    return filtered;
  }, [models, filterName, filterType, filterCategory, sortField, sortOrder]);

  const handleSort = (field: 'name' | 'category') => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> disabled
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // Disable sort
        setSortField(null);
        setSortOrder('asc');
      }
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const rows = filteredModels.map((model) => (
    <Table.Tr key={model.name}>
      <Table.Td>{model.name}</Table.Td>
      <Table.Td ta="center">
        <Menu shadow="md" withArrow>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              aria-label={intl.formatMessage({ id: 'actions' })}
              color="gray"
            >
              <IconSettings size={18} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            {model.type === 'CONTRACT' && (
              <Menu.Item
                leftSection={<IconFilePlus size={18} />}
                onClick={() => {
                  navigate(
                    routes.routes.createDocument(username as string, model.id)
                  );
                }}
              >
                {intl.formatMessage({ id: 'create' })}
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconFileImport size={18} />}
              onClick={() => setImportFieldsItem(model)}
            >
              {intl.formatMessage({ id: 'importFields' })}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconEdit size={18} />}
              onClick={() => {
                navigate(routes.routes.editModel(username as string, model.id));
              }}
            >
              {intl.formatMessage({ id: 'edit' })}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCopyPlus size={18} />}
              onClick={() => handleDuplicate(model)}
            >
              {intl.formatMessage({ id: 'duplicate' })}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconPencil size={18} />}
              onClick={() => setRenameModel(model)}
            >
              {intl.formatMessage({ id: 'rename' })}
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={18} />}
              onClick={() => setDeleteModel(model)}
            >
              {intl.formatMessage({ id: 'delete' })}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
      <Table.Td ta="center">{intl.formatMessage({ id: model.type })}</Table.Td>
      <Table.Td ta="center">{model.category}</Table.Td>
      <Table.Td ta="center">
        {timeAgo.format(new Date(model.updatedAt))}
      </Table.Td>
    </Table.Tr>
  ));

  if (isLoadingModels) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl" pb={200}>
      <Group justify="space-between" align="center" mb="md">
        <Title order={4}>{intl.formatMessage({ id: 'models' })}</Title>

        <Button
          id="add-new-model-button"
          variant="accent"
          onClick={() => {
            navigate(routes.routes.newModel(username as string));
          }}
        >
          {intl.formatMessage({ id: 'addModel' })}
        </Button>
      </Group>

      <Stack gap="md" mb="md">
        <Group gap="md">
          <TextInput
            label={intl.formatMessage({ id: 'name' })}
            placeholder={intl.formatMessage({ id: 'name' })}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            w={200}
            size="sm"
          />
          <NativeSelect
            label={intl.formatMessage({ id: 'type' })}
            data={[
              { value: '', label: intl.formatMessage({ id: 'all' }) },
              ...uniqueTypes.map((type) => ({
                value: type,
                label: intl.formatMessage({ id: type }),
              })),
            ]}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            w={150}
            size="sm"
          />
          <NativeSelect
            label={intl.formatMessage({ id: 'category' })}
            data={[
              { value: '', label: intl.formatMessage({ id: 'all' }) },
              ...uniqueCategories.map((category) => ({
                value: category,
                label: category,
              })),
            ]}
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            w={200}
            size="sm"
          />
        </Group>
      </Stack>

      <Table.ScrollContainer minWidth={1200}>
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          layout="auto"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Group
                  gap={4}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('name')}
                >
                  {intl.formatMessage({ id: 'name' })}
                  {sortField === 'name' && sortOrder === 'asc' && (
                    <IconSortAscending size={16} />
                  )}
                  {sortField === 'name' && sortOrder === 'desc' && (
                    <IconSortDescending size={16} />
                  )}
                </Group>
              </Table.Th>
              <Table.Th ta="center" w={100}>
                {intl.formatMessage({ id: 'actions' })}
              </Table.Th>
              <Table.Th ta="center" w={150}>
                {intl.formatMessage({ id: 'type' })}
              </Table.Th>
              <Table.Th ta="center" w={150}>
                <Group
                  gap={4}
                  justify="center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('category')}
                >
                  {intl.formatMessage({ id: 'category' })}
                  {sortField === 'category' && sortOrder === 'asc' && (
                    <IconSortAscending size={16} />
                  )}
                  {sortField === 'category' && sortOrder === 'desc' && (
                    <IconSortDescending size={16} />
                  )}
                </Group>
              </Table.Th>
              <Table.Th ta="center" w={150}>
                {intl.formatMessage({ id: 'updatedAt' })}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {deleteModel && (
        <DeleteModal
          model={deleteModel}
          onClose={async () => {
            // Refresh models from Redux after deletion
            if (username) {
              await dispatch(fetchModels(username));
            }
            setDeleteModel(null);
          }}
        />
      )}

      {renameModel && (
        <RenameModal
          model={renameModel}
          onClose={async (response: any) => {
            if (response) {
              // Refresh models from Redux after rename
              if (username) {
                await dispatch(fetchModels(username));
              }
            }
            setRenameModel(null);
          }}
        />
      )}

      {importFieldsItem && (
        <ImportFieldsModal
          model={importFieldsItem}
          onClose={() => {
            setImportFieldsItem(null);
          }}
        />
      )}
    </Container>
  );
};
